/**
 * Toast Notification Component
 * Modern slide-down notification for success messages
 */

import { useCallback, useEffect, useState } from "react";

export interface ToastProps {
  message: string;
  show: boolean;
  onClose: () => void;
  duration?: number;
}

export function Toast({ message, show, onClose, duration = 3500 }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    // Wait for exit animation to complete
    setTimeout(() => {
      setIsVisible(false);
      setIsExiting(false);
      onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (show) {
      // Trigger slide-in animation
      setIsVisible(true);
      setIsExiting(false);

      // Auto-dismiss after duration
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [show, duration, handleClose]);

  if (!show && !isVisible) return null;

  return (
    <div
      className={"fixed top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ease-out " + (
        isVisible && !isExiting
          ? "opacity-100 translate-y-0"
          : "-translate-y-full opacity-0"
      )}
      style={{
        transform: isVisible && !isExiting 
          ? "translateX(-50%) translateY(0)" 
          : "translateX(-50%) translateY(-100%)",
      }}
    >
      <div className="bg-hmh-blue text-white px-6 py-4 rounded-lg shadow-xl flex items-center gap-3 min-w-[320px] max-w-md">
        {/* Success checkmark icon */}
        <div className="flex-shrink-0">
          <svg
            className="h-6 w-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        {/* Message */}
        <p className="flex-1 font-medium text-sm">{message}</p>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-white/80 hover:text-white transition-colors"
          aria-label="Close notification"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
